import { api } from '../api/client';
import {
  beginVaultRecoverySetup,
  completeVaultRecoverySetup,
  isVaultRecoverySetupPending,
} from '../app/vaultSetupRecovery';
import { getAppState, markVaultUnlocked, refreshSession } from '../app';
import { createAndPublishRecoveryPackage } from '../crypto/recoveryFlow';
import { resolvePasswordPolicy } from '../crypto/passwordPoliciesApi';
import { defaultPolicyForTarget } from '../crypto/passwordPolicy';
import { setupVault, setStoredVaultVersion } from '../crypto/vault';
import { loadVaultSession, storeVaultSession } from '../crypto/vaultSession';
import { applyI18n, t } from '../i18n';
import { setCryptoBusy } from '../ui/cryptoBusy';
import { iconImg } from '../ui/icons';
import { attachPassphraseWhitespaceWarnings } from '../ui/passphraseWhitespaceWarning';
import {
  bindPasswordPolicyToForm,
  passwordPolicyErrorFromCause,
  validatePasswordForUi,
} from '../ui/passwordPolicyUi';
import { showRecoveryExportDialog } from '../ui/recoveryExportDialog';
import { createHelpTip } from '../help/helpTips';

export function VaultSetupPage(): HTMLElement {
  const panel = document.createElement('section');
  panel.className = 'center-panel';
  panel.setAttribute('data-help-context', 'vault');

  function finishRecoverySetup(): void {
    completeVaultRecoverySetup();
    history.pushState({}, '', '/');
  }

  function renderRecoveryStep(): void {
    panel.innerHTML = `
      <h1 class="panel-title" id="vault-setup-recovery-title">
        <span data-i18n="vault.setup.recovery.title"></span>
      </h1>
      <p class="muted" data-i18n="vault.setup.recovery.subtitle"></p>
      <div class="warning-panel">
        <span class="warning-panel__icon"></span>
        <span data-i18n="vault.setup.recovery.warning"></span>
      </div>
      <div class="form-grid vault-setup-recovery-actions">
        <button type="button" class="button primary" id="vault-setup-recovery-create">
          <span class="button__icon"></span>
          <span data-i18n="vault.setup.recovery.create"></span>
        </button>
        <button type="button" class="button ghost" id="vault-setup-recovery-skip">
          <span data-i18n="vault.setup.recovery.skip"></span>
        </button>
      </div>
      <p class="muted" data-i18n="vault.setup.recovery.skipHint"></p>
      <p class="error-text" id="vault-setup-recovery-error" hidden></p>
    `;

    const title = panel.querySelector('#vault-setup-recovery-title') as HTMLHeadingElement;
    title.prepend(iconImg('key', 28));
    title.append(createHelpTip('recovery'));
    panel.setAttribute('data-help-context', 'recovery');
    panel.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
    panel.querySelector('.button__icon')?.append(iconImg('key', 18));

    const createButton = panel.querySelector('#vault-setup-recovery-create') as HTMLButtonElement;
    const skipButton = panel.querySelector('#vault-setup-recovery-skip') as HTMLButtonElement;
    const error = panel.querySelector('#vault-setup-recovery-error') as HTMLParagraphElement;
    const actions = panel.querySelector('.vault-setup-recovery-actions') as HTMLDivElement;

    createButton.addEventListener('click', () => {
      void (async () => {
        error.hidden = true;

        const state = getAppState();
        if (state.kind !== 'authenticated') {
          return;
        }

        const session = loadVaultSession();
        if (!session) {
          error.hidden = false;
          error.textContent = t('status.error');
          return;
        }

        setCryptoBusy(panel, actions, true, {
          titleKey: 'settings.recovery.working',
          detailKey: 'settings.recovery.workingDetail',
          icon: 'hourglass',
        });

        try {
          const created = await createAndPublishRecoveryPackage(
            state.session.userId,
            session.vaultKey,
          );
          setCryptoBusy(panel, actions, false, {
            titleKey: 'settings.recovery.working',
            detailKey: 'settings.recovery.workingDetail',
          });
          await showRecoveryExportDialog(created.exportText);
          finishRecoverySetup();
        } catch (cause) {
          console.error('Vault setup recovery package failed', cause);
          error.hidden = false;
          error.textContent = t('status.error');
        } finally {
          setCryptoBusy(panel, actions, false, {
            titleKey: 'settings.recovery.working',
            detailKey: 'settings.recovery.workingDetail',
          });
        }
      })();
    });

    skipButton.addEventListener('click', () => {
      finishRecoverySetup();
    });

    applyI18n(panel);
  }

  function renderCreateForm(): void {
    panel.innerHTML = `
      <p class="muted" data-i18n="vault.setup.subtitle"></p>
      <div class="warning-panel">
        <span class="warning-panel__icon"></span>
        <span data-i18n="vault.setup.warning"></span>
      </div>
      <form class="form-grid" id="vault-setup-form" method="post" action="#">
        <label>
          <span data-i18n="vault.setup.passphrase"></span>
          <input type="password" name="passphrase" minlength="12" required autocomplete="new-password" />
        </label>
        <label>
          <span data-i18n="vault.setup.confirm"></span>
          <input type="password" name="confirm" minlength="12" required autocomplete="new-password" />
        </label>
        <p class="muted" id="vault-setup-policy-hint" hidden></p>
        <button type="submit" class="button primary">
          <span class="button__icon"></span>
          <span data-i18n="vault.setup.submit"></span>
        </button>
      </form>
      <p class="error-text" id="vault-setup-error" hidden></p>
    `;

    const heading = document.createElement('h1');
    heading.className = 'panel-title';
    const title = document.createElement('span');
    title.setAttribute('data-i18n', 'vault.setup.title');
    heading.append(iconImg('lock', 28), title, createHelpTip('vault'));
    panel.prepend(heading);
    panel.setAttribute('data-help-context', 'vault');

    panel.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
    panel.querySelector('.button__icon')?.append(iconImg('key', 18));

    const form = panel.querySelector('#vault-setup-form') as HTMLFormElement;
    const error = panel.querySelector('#vault-setup-error') as HTMLParagraphElement;
    const policyHint = panel.querySelector('#vault-setup-policy-hint') as HTMLElement;
    const passphraseInput = form.querySelector('input[name="passphrase"]') as HTMLInputElement;
    const confirmInput = form.querySelector('input[name="confirm"]') as HTMLInputElement;
    attachPassphraseWhitespaceWarnings(passphraseInput, confirmInput);

    let activePolicy = defaultPolicyForTarget('vaultPassphrase');
    void resolvePasswordPolicy('vaultPassphrase').then((policy) => {
      activePolicy = policy;
      bindPasswordPolicyToForm({
        policy,
        inputs: [passphraseInput, confirmInput],
        hint: policyHint,
      });
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void (async () => {
        error.hidden = true;
        const data = new FormData(form);
        const passphrase = String(data.get('passphrase') ?? '');
        const confirm = String(data.get('confirm') ?? '');

        if (passphrase !== confirm) {
          error.hidden = false;
          error.textContent = t('vault.setup.mismatch');
          return;
        }

        const policyError = validatePasswordForUi(passphrase, activePolicy);
        if (policyError) {
          error.hidden = false;
          error.textContent = policyError;
          return;
        }

        setCryptoBusy(panel, form, true, {
          titleKey: 'vault.setup.working',
          detailKey: 'vault.setup.workingDetail',
          icon: 'hourglass',
        });

        try {
          const result = await setupVault(passphrase, activePolicy);
          await api.setupVault({
            vaultBlob: result.vaultBlob,
            vaultSalt: result.vaultSalt,
            vkWrapPassphrase: result.vkWrapPassphrase,
            userPublicKey: result.userPublicKey,
            vaultVersion: 1,
          });

          storeVaultSession(result.vaultKey, result.userPrivateKey);
          await refreshSession();
          const session = await api.getSession();
          setStoredVaultVersion(session.userId, 1);
          markVaultUnlocked();
          beginVaultRecoverySetup();
          renderRecoveryStep();
        } catch (cause) {
          console.error('Vault setup failed', cause);
          error.hidden = false;
          error.textContent =
            passwordPolicyErrorFromCause(cause, activePolicy) ?? t('status.error');
        } finally {
          setCryptoBusy(panel, form, false, {
            titleKey: 'vault.setup.working',
            detailKey: 'vault.setup.workingDetail',
          });
        }
      })();
    });

    applyI18n(panel);
  }

  const state = getAppState();
  if (state.kind === 'authenticated' && state.session.vaultSetupComplete && isVaultRecoverySetupPending()) {
    renderRecoveryStep();
  } else {
    renderCreateForm();
  }

  return panel;
}
